"use client";

import * as React from "react";
import { cn } from "../lib";

export interface PulseRingProps {
	className?: string;
	/**
	 * Seconds to offset the animation start (useful for staggering multiple rings).
	 */
	delay?: number;
	/**
	 * Base diameter of the ring in pixels.
	 */
	sizePx?: number;
	/**
	 * Border width in pixels.
	 */
	borderPx?: number;
	/**
	 * Border color (CSS color).
	 */
	borderColor?: string;
	/**
	 * Animation duration in seconds.
	 */
	durationS?: number;
}

/**
 * A lightweight, purely-CSS pulsing ring effect designed to sit absolutely
 * positioned over an interactable. Intended usage: render as a child of a
 * `relative` container.
 */
export const PulseRing = React.memo(function PulseRing({
	className,
	delay = 0,
	sizePx = 32,
	borderPx = 2,
	borderColor = "rgba(0, 0, 0, 0.4)",
	durationS = 5,
}: PulseRingProps) {
	// Keep the injected CSS stable; only depend on tunable props.
	const css = React.useMemo(
		() => `
@keyframes sq-pulse-ring-scale {
	from { transform: translate(-50%, -50%) scale(0.5); opacity: 0.6; }
	to { transform: translate(-50%, -50%) scale(2.5); opacity: 0; }
}
.sq-pulse-ring-circle {
	position: absolute;
	top: 50%;
	left: 50%;
	/* Match keyframe "from" before animation runs / during delay (fill-mode none leaves
	   top/left anchoring the box's top-left at center → ring flashes bottom-right). */
	transform: translate(-50%, -50%) scale(0.5);
	opacity: 0.6;
	border-radius: 50%;
	pointer-events: none;
	animation: sq-pulse-ring-scale ${durationS}s infinite cubic-bezier(0.4, 0, 0.6, 1);
	animation-fill-mode: backwards;
}
`,
		[durationS],
	);

	const circleStyle: React.CSSProperties = {
		border: `${borderPx}px solid ${borderColor}`,
		width: sizePx,
		height: sizePx,
	};

	return (
		<>
			<style>{css}</style>
			<div
				className={cn(
					"pointer-events-none absolute inset-0",
					className,
				)}
			>
				<div
					className="sq-pulse-ring-circle"
					style={{ ...circleStyle, animationDelay: `${delay}s` }}
				/>
				<div
					className="sq-pulse-ring-circle"
					style={{
						...circleStyle,
						animationDelay: `${delay + 0.5}s`,
					}}
				/>
				<div
					className="sq-pulse-ring-circle"
					style={{ ...circleStyle, animationDelay: `${delay + 1}s` }}
				/>
				<div
					className="sq-pulse-ring-circle"
					style={{
						...circleStyle,
						animationDelay: `${delay + 1.5}s`,
					}}
				/>
			</div>
		</>
	);
});
